import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HiMail, HiLockClosed, HiUser, HiPhone } from "react-icons/hi";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { Combobox } from "@headlessui/react";
import axios from "axios";
import DealVisorLogo from "../../assets/Images/Deal_visor.png";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { toast, ToastContainer } from "react-toastify";
import {
  COUNTRIES_API,
  CREATEORUPDATE_USERS_API,
  SEND_SIGNUP_EMAIL_API,
} from "../../Constants/apiRoutes";
import LoadingAnimation from "../../Components/Loading/LoadingAnimation";
import md5 from "md5";

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    Email: "",
    Password: "",
    PhoneNumber: "",
    CountryID: "",
    BusinessUnit: "",
    Region: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [query, setQuery] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState({
    name: "",
    email: "",
  });
  const features = [
    {
      icon: "📄",
      title: "Document Upload",
      description: "Securely upload and manage your documents",
    },
    {
      icon: "✍️",
      title: "Digital Signatures",
      description: "Sign documents electronically",
    },
    {
      icon: "👥",
      title: "Collaboration",
      description: "Review and approve documents together",
    },
    {
      icon: "🔒",
      title: "Secure Storage",
      description: "Keep your documents safe and organized",
    },
  ];

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(COUNTRIES_API);
      setCountries(response.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch countries.");
    }
  };

  const handleCountryChange = (selectedCountry) => {
    setSelectedCountry(selectedCountry);
    setFormData((prevFormData) => ({
      ...prevFormData,
      CountryID: selectedCountry?.CountryID || "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.FirstName.trim())
      newErrors.FirstNameError = "First name is required";
    if (!formData.LastName.trim())
      newErrors.LastNameError = "Last name is required";
    if (!formData.Email.trim()) newErrors.EmailError = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(formData.Email))
      newErrors.EmailError = "Email is invalid";
    if (!formData.Password) newErrors.PasswordError = "Password is required";
    else if (formData.Password.length < 6)
      newErrors.PasswordError = "Password must be at least 6 characters";
    if (!formData.CountryID) newErrors.CountryIdError = "Country is required";
    if (!formData.BusinessUnit.trim())
      newErrors.BusinessUnitError = "Business unit is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const formDataToSend = { ...formData };
      formDataToSend.CountryID = selectedCountry?.CountryID || "";
      formDataToSend.IsInternal = true;
      formDataToSend.TenantID = 1;
      formDataToSend.Password = md5(formData.Password);

      const response = await axios({
        method: "post",
        url: CREATEORUPDATE_USERS_API,
        data: formDataToSend,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.StatusCode === "SUCCESS") {
        toast.success("User registered successfully!");
        try {
          await axios.post(SEND_SIGNUP_EMAIL_API, {
            Email: formData.Email,
          });
          toast.success("Email Sent successfully!");
        } catch (emailError) {
          toast.warning("User registered, but email sending failed.");
        }
        setSuccessData({
          email: formData.Email,
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  const SuccessModal = ({ onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
        <div className="flex justify-center mb-4">
          <div className="bg-green-100 p-3 rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
        <h3 className="text-xl font-bold text-center text-gray-800 mb-2">
          Registration Successful!
        </h3>
        <p className="text-center text-gray-600 mb-4">
          Thank you, <span className="font-semibold">{successData.name}</span>!
        </p>
        <p className="text-center text-gray-600 mb-6">
          Your account has been created successfully. An admin will assign your
          role and contact you via email at{" "}
          <span className="font-semibold">{successData.email}</span>.
        </p>
        <div className="flex justify-center">
          <button
            onClick={onClose}
            className="bg-[#8B4513] hover:bg-[#6d360d] text-white font-medium py-2 px-6 rounded-lg transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen relative flex overflow-hidden">
      {/* Background Pattern with Gradient */}{" "}
      {isLoading && <LoadingAnimation />}
      <ToastContainer />
      {showSuccessModal && (
        <SuccessModal
          onClose={() => {
            setShowSuccessModal(false);
            navigate("/");
          }}
        />
      )}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-white opacity-80" />

        {/* Animation Layer - Using GIF */}
        <div className="absolute inset-0 z-10 opacity-75 align-center">
          <DotLottieReact
            src="https://lottie.host/79fbfbdd-bf0f-4478-9136-b101cd217fe8/T7NlAlJBhK.lottie"
            loop={true}
            autoplay={true}
            className="absolute inset-0 h-full w-full object-contain shadow-sm"
          />
        </div>

        {/* Pattern Overlay */}
        <div className="absolute inset-0 bg-pattern opacity-10" />
      </div>
      {/* Content */}
      <div className="relative z-20 flex w-full">
        {/* Left Side - Visible on tablet and desktop */}
        <div className="flex-1 p-8 lg:p-12 hidden sm:block">
          <div className="flex items-center gap-10">
            {/* Logo */}
            <img
              src={DealVisorLogo}
              alt="DealVisorLogo"
              className="h-28 w-auto"
            />

            {/* Heading */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-[#8B4513]">
              Create your account
            </h1>
          </div>
          <div className="text-[#301607] max-w-xl mt-10">
            {/* Features Grid - Updated background and text colors */}
            <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-6 mt-40">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 bg-[#8B4513] rounded-lg p-4 transition-all duration-300 backdrop-blur-sm 
      border border-[#8B4513]"
                >
                  <div className="text-white text-2xl shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-white text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Sign Up Form */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
          <div className="bg-[#8B4513] rounded-3xl p-6 sm:p-8 w-full max-w-[600px] shadow-xl backdrop-blur-lg">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* First Name */}
                <div className="space-y-2">
                  <label className="text-[#fff] text-sm">First Name</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="FirstName"
                      value={formData.FirstName}
                      onChange={handleChange}
                      className="w-full p-3 pl-10 bg-[#fff] rounded-lg text-gray-700"
                      placeholder="John"
                    />
                    <HiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  </div>
                  {errors.FirstNameError && (
                    <p className="text-white text-xs">
                      {errors.FirstNameError}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div className="space-y-2">
                  <label className="text-[#fff] text-sm">Last Name</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="LastName"
                      value={formData.LastName}
                      onChange={handleChange}
                      className="w-full p-3 pl-10 bg-[#fff] rounded-lg text-gray-700"
                      placeholder="Doe"
                    />
                    <HiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  </div>
                  {errors.LastNameError && (
                    <p className="text-white text-xs">{errors.LastNameError}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Email */}
                <div className="space-y-2">
                  <label className="text-[#fff] text-sm">Email</label>
                  <div className="relative">
                    <input
                      type="email"
                      name="Email"
                      value={formData.Email}
                      onChange={handleChange}
                      className="w-full p-3 pl-10 bg-[#fff] rounded-lg text-gray-700"
                      placeholder="john@example.com"
                    />
                    <HiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  </div>
                  {errors.EmailError && (
                    <p className="text-white text-xs">{errors.EmailError}</p>
                  )}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className="text-[#fff] text-sm">Password</label>
                  <div className="relative">
                    <input
                      type="password"
                      name="Password"
                      value={formData.Password}
                      onChange={handleChange}
                      className="w-full p-3 pl-10 bg-[#fff] rounded-lg text-gray-700"
                      placeholder="••••••••"
                    />
                    <HiLockClosed className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  </div>
                  {errors.PasswordError && (
                    <p className="text-white text-xs">{errors.PasswordError}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Phone Number */}
                <div className="space-y-2">
                  <label className="text-[#fff] text-sm">Phone Number</label>
                  <div className="relative">
                    <input
                      type="tel"
                      name="PhoneNumber"
                      value={formData.PhoneNumber}
                      onChange={handleChange}
                      className="w-full p-3 pl-10 bg-[#fff] rounded-lg text-gray-700"
                      placeholder="+1234567890"
                    />
                    <HiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                  </div>
                </div>

                {/* Business Unit */}
                <div className="space-y-2">
                  <label className="text-[#fff] text-sm">Business Unit</label>
                  <input
                    type="text"
                    name="BusinessUnit"
                    value={formData.BusinessUnit}
                    onChange={handleChange}
                    className="w-full p-3 bg-[#fff] rounded-lg text-gray-700"
                    placeholder="Your business unit"
                  />
                  {errors.BusinessUnitError && (
                    <p className="text-white text-xs">
                      {errors.BusinessUnitError}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[#fff] text-sm">Region</label>
                  <input
                    type="text"
                    id="Region"
                    name="Region"
                    value={formData.Region || ""}
                    onChange={handleChange}
                    required
                    className="w-full p-3 bg-[#fff] rounded-lg text-gray-700"
                  />
                </div>
                {/* Country Dropdown */}
                <div className="space-y-2">
                  <label className="text-[#fff] text-sm">Country</label>
                  <Combobox
                    as="div"
                    value={selectedCountry}
                    onChange={handleCountryChange}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className={`block w-full rounded-md py-2 px-4 shadow-sm sm:text-sm h-12 ${
                          !formData.CountryID && errors.CountryIdError
                            ? "border-2 border-white"
                            : "border border-gray-400"
                        }`}
                        onChange={(event) => setQuery(event.target.value)}
                        displayValue={(country) => country?.CountryName || ""}
                        placeholder="Select country"
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                        <ChevronUpDownIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </Combobox.Button>

                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {countries
                          .filter((country) =>
                            country.CountryName.toLowerCase().includes(
                              query.toLowerCase()
                            )
                          )
                          .map((country) => (
                            <Combobox.Option
                              key={country.CountryID}
                              value={country}
                              className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-indigo-600 hover:text-white"
                            >
                              <span className="block truncate font-normal group-data-[selected]:font-semibold">
                                {country.CountryName}
                              </span>
                              <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-indigo-600 group-data-[selected]:flex group-data-[focus]:text-white">
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            </Combobox.Option>
                          ))}
                      </Combobox.Options>
                    </div>
                  </Combobox>
                  {!formData.CountryID && errors.CountryIdError && (
                    <p className="text-white text-xs">
                      {errors.CountryIdError}
                    </p>
                  )}
                </div>
              </div>
              {/* Sign Up Button */}
              <button
                type="submit"
                className="w-2/3 mx-auto bg-[#fff] text-[#8B4513] py-3 rounded-lg font-medium transition-colors mt-6 block"
                disabled={isLoading}
              >
                Sign Up
              </button>

              <p className="text-white text-center">
                Already have an account?{" "}
                <a onClick={() => navigate("/")} className="underline">
                  Log in
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
